import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {Component, Input, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {PaginationModel} from '../../../models/file-table.model';
import {TaskModel} from '../../../models/task.model';
import {BoardGroupModel, BoardTableModel} from '../../../models/tasktable.model';
import {MemberModel} from '../../../models/user.model';
import {CrmService} from '../../../services/api/crm.service';
import {AlertService} from '../../../services/notify/alert.service';
import {StatisticService} from '../../../services/statistic.service';
import {buildItemsMap} from '../../calendar/calendar-utils';
import {CalendarItem, CalendarLegend} from '../../calendar/calendar.model';
import {ComponentsModule} from '../../components.module';

type TaskStatistic = {
    important: number,
    middle: number,
    normal: number
}

@Component({
    selector: 'app-tasks-index-tab',
    standalone: true,
    imports: [
        ComponentsModule,
        DatePipe,
        NgTemplateOutlet,
        TranslatePipe
    ],
    templateUrl: './tasks-index-tab.component.html',
    styleUrl: './tasks-index-tab.component.scss'
})
export class TasksIndexTabComponent implements OnInit {
    @Input() currentMember: MemberModel;

    tab: 0 | 1 | 2 = 0;

    tableTab: 'my' | string = 'my';

    allTasksForCalendar: TaskModel[];

    public defaultPagination: PaginationModel = {
        limit: 5,
        current_page: 1,
        contract_is_empty: false,
        sort_by: 'updated_at',
        sort: 'desc',
    };

    allTasksTable: {
        [key: 'my' | string]: {
            pagination: PaginationModel,
            tasks: TaskModel[][],
        }
    };

    allBoards: BoardTableModel[];

    calendarLegend: CalendarLegend[];
    today = new Date();
    itemsMap: Record<string, CalendarItem[]> = {};

    statisticData: TaskStatistic;

    constructor(
        private alertService: AlertService,
        private router: Router,
        private translate: TranslateService,
        private crmService: CrmService,
        private statistic: StatisticService,
    ) {
        this.calendarLegend = [
            {
                type: 'light',
                title: this.translate.instant('INDEX_PAGE.TASKS.TEXT.LEGEND.NEW'),
            },
            {
                type: 'blue',
                title: this.translate.instant('INDEX_PAGE.TASKS.TEXT.LEGEND.IN_PROGRESS'),
            },
            {
                type: 'green',
                title: this.translate.instant('INDEX_PAGE.TASKS.TEXT.LEGEND.COMPLETED'),
            },
            {
                type: 'red',
                title: this.translate.instant('INDEX_PAGE.TASKS.TEXT.LEGEND.EXPIRED'),
            }
        ];

        this.getStatistic();
    }

    ngOnInit(): void {
        this.getAllMyTasks();
        this.getBoards();
    }

    getStatistic(): void {
        this.statistic.getIndexPageStatistics('tasks').subscribe({
            next: res => {
                this.statisticData = res.tasks as TaskStatistic;
            }
        });
    }

    getBoards(): void {
        this.crmService.getAllBoards().subscribe(res => {
            this.allBoards = res.boards;

            res.groups.map((x: BoardGroupModel) => {
                this.allBoards = this.allBoards.concat(x.boards);
            });
        });
    }

    getAllMyTasks(isRefreshCalendar = false): void {
        const pagination = {
            limit: 50,
            current_page: 1,
            contract_is_empty: false,
            sort_by: 'updated_at',
            sort: 'desc',
        };

        this.crmService.getAllTasksByPages(this.currentMember.guid, null, pagination).subscribe({
            next: res => {
                this.allTasksForCalendar = res;
                if (!isRefreshCalendar) {
                    this.allTasksTable = {
                        [this.tableTab]: {
                            pagination: structuredClone(this.defaultPagination),
                            tasks: []
                        }
                    };

                    this.allTasksTable[this.tableTab].pagination.pages = Math.ceil(res.length / this.defaultPagination.limit);

                    this.allTasksTable[this.tableTab].tasks[1] = res.slice(0, 5);
                }
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    changeTab(tab: 'my' | string): void {
        if (this.allTasksTable[tab]) {
            this.allTasksTable[tab].pagination.current_page = 1;
            this.tableTab = tab;
            return;
        }

        const paginationWithBoard = structuredClone(this.defaultPagination);
        if (tab !== 'my') {
            paginationWithBoard.board_alias = tab;
        }

        this.allTasksTable[tab] = {
            pagination: paginationWithBoard,
            tasks: []
        };
        this.tableTab = tab;

        this.getMyTasksForTable();
    }

    changePage(page: number, refresh = false): void {
        this.allTasksTable[this.tableTab].pagination.current_page = page;

        if (refresh) {
            this.allTasksTable[this.tableTab].tasks = [];
        }
        if (!this.allTasksTable[this.tableTab].tasks[page]) {
            this.getMyTasksForTable();
        }
    }

    getMyTasksForTable(): void {
        const currentPagination = this.allTasksTable[this.tableTab].pagination;

        this.crmService.getTasks(this.currentMember.guid, null, currentPagination).subscribe({
            next: res => {
                this.allTasksTable[this.tableTab].pagination.pages = res.page_count;
                this.allTasksTable[this.tableTab].tasks[currentPagination.current_page] = res.data;
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    goTo(task: TaskModel): void {
        void this.router.navigate([`account/tasks/all/${task.task_board_name}`, task.task_alias]);
    }

    onDayClick(date: Date) {
        console.log('day clicked', date);
    }

    onItemClick(payload: { date: Date; item: CalendarItem }) {
        console.log('item clicked', payload);
    }

    onAddItems(startDate: Date, endDate: Date): void {
        const tasks = this.allTasksForCalendar.filter(x => x.deadline_at);

        const filtered: (CalendarItem & { __key: string })[] = [];

        for (const task of tasks) {
            const deadlineDate = new Date(new Date(task.deadline_at).toDateString());
            if (deadlineDate < startDate || deadlineDate > endDate) {
                continue;
            }

            let taskColor: 'light' | 'blue' | 'green' | 'red';
            const today = new Date(this.today.toDateString());

            if (deadlineDate < today) {
                if (task.status_current.is_finish) {
                    taskColor = 'green';
                } else {
                    taskColor = 'red';
                }
            } else {
                if (task.status_current.is_finish) {
                    taskColor = 'green';
                } else {
                    if (task.status_current.is_initial) {
                        taskColor = 'light';
                    } else {
                        taskColor = 'blue';
                    }
                }
            }

            const y = deadlineDate.getFullYear();
            const m = (deadlineDate.getMonth() + 1).toString().padStart(2, '0');
            const d = deadlineDate.getDate().toString().padStart(2, '0');
            const key = `${y}-${m}-${d}`;

            filtered.push({
                __key: key,
                id: task.task_alias,
                title: task.title,
                value: task.title,
                type: taskColor,
            } as any);
        }

        const newMap = buildItemsMap(filtered, this.itemsMap);

        if (newMap !== this.itemsMap) {
            this.itemsMap = newMap;
        }
    }

    scrollBlock(move: 'right' | 'left', scrollContainer: HTMLElement): void {
        if (move === 'left') {
            scrollContainer.scroll({left: scrollContainer.scrollLeft - 550});
        } else {
            scrollContainer.scroll({left: scrollContainer.scrollLeft + 550});
        }
    }
}
